import { bunja } from 'bunja';

import { TpSlTriggerTimeIndicatorMapper } from '@flipster/features/src/tpsl-modal/constant';
import { FormattedMessage, i18nBunja } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { css } from '@flipster/styled-system/css';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import Num from '@flipster/utils/num';
import { V2OrderSideType } from '@flipster/utils/types/base';

import { TimeTriggerTpslProps } from '@web/features/modals/TimeTriggerTpslModal/useTimeTriggerTpslModal';

export const helperBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const transPosition = getT('Position.r250401');
  const transCommon = getT('Common.r250401');
  const transKyc = getT('Kyc.r250311');
  const transAutogenCommon = getT('Autogen_common.r251126');

  const checkShowLiquidationAlert = (side: V2OrderSideType, triggerPrice: string, LiquidationPrice: string) => {
    // 청산가 근처 청산가에 2% 정도 근접 했을땐 alert 로 알려준다
    if (side === 'Long')
      return Num(triggerPrice).isLessThanOrEqualTo(Num(LiquidationPrice).dividedBy(100).multipliedBy(102));
    return Num(triggerPrice).isGreaterThanOrEqualTo(Num(LiquidationPrice).dividedBy(100).multipliedBy(98));
  };

  const liquidationAlert = (onConfirm: () => unknown) =>
    dialog.open({
      title: {
        label: transAutogenCommon('s_Liquidation_notice'),
      },
      content: (
        <div>
          <FormattedMessage id='Autogen_common.r251126.s_Your_trigger_price_is_close_to_liquidation_price' />{' '}
          <FormattedMessage
            id='Trade.r260625.Liquidation_notice_message_if_reaches_mark_link'
            values={{
              markPrice: (
                <IsomorphicLink
                  href='https://flipsterxyz.zendesk.com/hc/en-us/articles/7353316085647-Mark-Price-Calculation'
                  className={css({ color: 'content.default.level-5', textDecoration: 'underline' })}
                  external
                  newTab
                >
                  <FormattedMessage id='Autogen_common.r251126.s_Mark_Price_Link' />
                </IsomorphicLink>
              ),
            }}
          />
        </div>
      ),
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label={transKyc('Proceed')} onClick={onConfirm} />,
        secondary: <Dialog.Button hierarchy='secondary' color='neutral' label={transCommon('Cancel')} />,
      },
    });

  const deleteTpSlAlert = (tpSlInfo: TimeTriggerTpslProps, onConfirm: () => unknown) => {
    dialog.open({
      title: {
        label: transPosition(tpSlInfo.tpSlType === 'tp' ? 'Remove_tp_title' : 'Remove_sl_title'),
      },
      content: transPosition(tpSlInfo.tpSlType === 'tp' ? 'Remove_tp_description' : 'Remove_sl_description'),
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label='Confirm' onClick={onConfirm} />,
        secondary: <Dialog.Button hierarchy='secondary' color='neutral' label={transCommon('Cancel')} />,
      },
    });
  };

  const tpSlErrorHandler = (e: unknown, onClose: () => unknown) => {
    const errorCode = getErrorCode(e);

    snackbar.error({ message: getErrorMessage(e) });
    switch (errorCode) {
      case 'UnknownPosition':
      case 'InvalidTriggerPriceRatio':
        onClose();
        break;
      default:
        break;
    }
  };

  const getTriggerTimeDirection = (side: V2OrderSideType, tpSlType: 'tp' | 'sl'): 'Up' | 'Down' =>
    TpSlTriggerTimeIndicatorMapper[side][tpSlType] === '+' ? 'Up' : 'Down';

  const adjustTriggerTimeTpSlPercentForServer = (side?: V2OrderSideType, tpSlType?: 'tp' | 'sl', percent?: string) => {
    if (!(side && tpSlType && percent)) return '';
    return (
      TpSlTriggerTimeIndicatorMapper[side][tpSlType] === '+'
        ? Num(1).plus(Num(percent).dividedBy(100))
        : Num(1).minus(Num(percent).dividedBy(100))
    ).toString();
  };

  const adjustTriggerTimeTpSlRatioForUserInput = (side?: V2OrderSideType, tpSlType?: 'tp' | 'sl', ratio?: string) => {
    if (!(side && tpSlType && ratio)) return '';
    return (
      TpSlTriggerTimeIndicatorMapper[side][tpSlType] === '+' ? Num(ratio).minus(1) : Num(ratio).multipliedBy(-1).plus(1)
    )
      .multipliedBy(100)
      .toString();
  };

  return {
    checkShowLiquidationAlert,
    liquidationAlert,
    deleteTpSlAlert,
    tpSlErrorHandler,
    getTriggerTimeDirection,
    adjustTriggerTimeTpSlPercentForServer,
    adjustTriggerTimeTpSlRatioForUserInput,
  };
});
helperBunja.debugLabel = 'helperBunja';
